/**
 * useTokenSearch Hook - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - Manages token search and filtering for script blocks.
 */

import { useState, useMemo, useCallback } from 'react';

/**
 * Hook for token search/filtering in script blocks
 * @param {Array} formFields - Data fields
 * @param {Array} quoteFields - Quote sheet fields
 */
export function useTokenSearch(formFields, quoteFields) {
  const [tokenSearch, setTokenSearch] = useState("");

  const tokenSearchValue = tokenSearch.trim().toLowerCase();

  const tokenMatches = useCallback(
    (label) => {
      if (!tokenSearchValue) return true;
      return String(label || "").toLowerCase().includes(tokenSearchValue);
    },
    [tokenSearchValue]
  );

  // Data fields filtered and sorted
  const tokenDataFields = useMemo(() => {
    const list = [...(formFields || [])]
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }));
    return list.filter((f) => tokenMatches(f.label));
  }, [formFields, tokenMatches]);

  // Quote input fields filtered and sorted
  const tokenQuoteInputs = useMemo(() => {
    const list = [...(quoteFields || [])]
      .filter((f) => f.kind === "input")
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }));
    return list.filter((f) => tokenMatches(f.label));
  }, [quoteFields, tokenMatches]);

  // Quote calculated fields filtered and sorted
  const tokenQuoteCalculated = useMemo(() => {
    const list = [...(quoteFields || [])]
      .filter((f) => f.kind === "calculated")
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }));
    return list.filter((f) => tokenMatches(f.label));
  }, [quoteFields, tokenMatches]);

  // Quote options (Option 1, 2, 3 x each input field)
  const tokenQuoteOptions = useMemo(() => {
    const base = [...(quoteFields || [])]
      .filter((f) => f.kind === "input")
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || ""), undefined, { sensitivity: "base" }));
    const out = [];
    [1, 2, 3].forEach((opt) => {
      base.forEach((f) => {
        const label = `Option ${opt} — ${f.label}`;
        if (tokenMatches(label) || tokenMatches(f.label)) {
          out.push({ opt, field: f, label });
        }
      });
    });
    return out.sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: "base" }));
  }, [quoteFields, tokenMatches]);

  // Agent variables
  const tokenAgentOptions = useMemo(() => {
    const items = [
      { value: "agent_var::full_name", label: "Agent Full Name" },
      { value: "agent_var::first_name", label: "Agent First Name" },
      { value: "agent_var::last_name", label: "Agent Last Name" },
    ];
    return items
      .filter((item) => tokenMatches(item.label))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [tokenMatches]);

  // AW-2C-005 -- the owned call's customer.
  //
  // Labelled "Customer ..." against the existing "Agent ..." entries on
  // purpose: the two are one word apart in the token syntax and
  // opposite in meaning, and the picker is where an author chooses
  // between them. The four ids match `CUSTOMER_TOKEN_IDS` in
  // `workspaceCustomerAdapters`; a test asserts they stay in step, so
  // the picker can never offer a token the resolver would refuse.
  const tokenCustomerOptions = useMemo(() => {
    const items = [
      { value: "customer_var::first_name", label: "Customer First Name" },
      { value: "customer_var::last_name", label: "Customer Last Name" },
      { value: "customer_var::city", label: "Customer City" },
      { value: "customer_var::state", label: "Customer State" },
    ];
    return items
      .filter((item) => tokenMatches(item.label))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [tokenMatches]);

  // Has any matches
  const hasTokenMatches =
    tokenDataFields.length +
    tokenQuoteInputs.length +
    tokenQuoteOptions.length +
    tokenAgentOptions.length +
    tokenCustomerOptions.length +
    tokenQuoteCalculated.length >
    0;

  return {
    tokenSearch,
    setTokenSearch,
    tokenDataFields,
    tokenQuoteInputs,
    tokenQuoteCalculated,
    tokenQuoteOptions,
    tokenAgentOptions,
    tokenCustomerOptions,
    hasTokenMatches,
  };
}

export default useTokenSearch;
